import { useNavigate } from "react-router-dom";
import { Box, Typography, Button, Grid, Paper } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Search } from "@mui/icons-material";
import { useHomePage } from "../hooks";
import { APP_TITLE } from "../constants";
import { StatsTable } from "../components/StatsTable";
import { AllianceCoverageTable } from "../components/AllianceCoverageTable";
import { LoadingSpinner } from "../components/LoadingSpinner";

export function HomePage() {
  const navigate = useNavigate();
  const { stats, allianceCoverage, loading } = useHomePage();

  return (
    <Box>
      <HeroSection stats={stats} onNavigate={navigate} />

      <Box sx={{ mb: { xs: 4, md: 5 } }}>
        <Box
          sx={{
            mb: 2,
            borderLeft: 4,
            borderColor: "secondary.main",
            pl: { xs: 1.5, sm: 2 },
          }}
        >
          <Typography
            variant="h3"
            sx={{
              mb: 1,
              fontSize: { xs: "1.2rem", sm: "1.35rem", md: "1.5rem" },
              lineHeight: 1.3,
            }}
          >
            Statistički pregled sustava
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              maxWidth: 900,
              fontSize: { xs: "0.875rem", sm: "1rem" },
            }}
          >
            Zbroj saveza u registru te koliko ih ima integraciju po modulima
            (detalji, vijesti, članstvo, dokumenti, rasporedi, pravilnici,
            reference, predstavljanje saveza, kontakt, adresa i ostale
            informacije). Kolona &ldquo;Stavke&rdquo; zbraja zapise u listama
            (npr. broj vijesti), a za objekte (detalji, kontakt, adresa, ostalo)
            ukupan broj ispunjenih polja kroz cijeli registar.
          </Typography>
        </Box>
        {loading && !stats ? (
          <LoadingSpinner py={4} />
        ) : (
          <StatsTable stats={stats} />
        )}
      </Box>

      <Box sx={{ mb: { xs: 3, md: 2 } }}>
        <Box
          sx={{
            mb: 2,
            borderLeft: 4,
            borderColor: "secondary.main",
            pl: { xs: 1.5, sm: 2 },
          }}
        >
          <Typography
            variant="h3"
            sx={{
              mb: 1,
              fontSize: { xs: "1.2rem", sm: "1.35rem", md: "1.5rem" },
              lineHeight: 1.3,
            }}
          >
            Pokrivenost po savezima
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              maxWidth: 900,
              fontSize: { xs: "0.8125rem", sm: "0.875rem" },
            }}
          >
            Svaki red je jedan savez iz registra. Stupac{" "}
            <strong>Postotak</strong> pokazuje koliki je udio područja (stupaca)
            ispunjenih barem jednim podatkom (uključuje i Ostalo / ostale
            informacije). Prazna ćelija znači da integracija za taj modul još
            nije donijela podatke.
          </Typography>
        </Box>
        {loading && allianceCoverage.length === 0 ? (
          <LoadingSpinner py={4} />
        ) : (
          <AllianceCoverageTable rows={allianceCoverage} />
        )}
      </Box>
    </Box>
  );
}

function HeroSection({
  stats,
  onNavigate,
}: {
  stats: any;
  onNavigate: (path: string) => void;
}) {
  const lastUpdatedLabel =
    stats?.lastUpdated != null
      ? new Date(stats.lastUpdated).toLocaleString("hr-HR", {
          day: "numeric",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "-";

  const rc = stats?.aggregates?.recordCounts ?? {};
  const newsN = rc.news ?? 0;
  const membersN = rc.members ?? 0;
  const documentsN = rc.documents ?? 0;

  return (
    <Paper
      elevation={0}
      sx={(theme) => ({
        bgcolor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        border: `1px solid ${theme.palette.divider}`,
        p: { xs: 2, sm: 3, md: 6 },
        borderRadius: { xs: 2, md: 3 },
        mb: { xs: 4, md: 5 },
        position: "relative",
        overflow: "hidden",
        ...(theme.palette.mode === "light"
          ? {
              backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(theme.palette.secondary.main, 0.04)} 100%)`,
            }
          : {}),
      })}
    >
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <Typography
          variant="h1"
          sx={(theme) => ({
            color:
              theme.palette.mode === "dark"
                ? "inherit"
                : theme.palette.text.primary,
            mb: 2,
            fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
            lineHeight: { xs: 1.25, sm: 1.2 },
          })}
        >
          {APP_TITLE}
        </Typography>
        <Typography
          variant="body1"
          sx={(theme) => ({
            color:
              theme.palette.mode === "dark"
                ? alpha(theme.palette.text.primary, 0.82)
                : theme.palette.text.secondary,
            maxWidth: 720,
            mb: 2,
            fontSize: { xs: "0.9rem", sm: "1rem" },
          })}
        >
          Integrirani sustav za praćenje, analizu i transparentnost hrvatskog
          sportskog sustava. Na početnoj ploči vidite koliko je saveza ušlo u
          registar, kada je zadnje ažuriranje podataka te koliko je stavki
          prikupljeno po glavnim područjima (vijesti, članstvo, dokumenti i
          sl.).
        </Typography>
        <Typography
          variant="body2"
          sx={(theme) => ({
            color:
              theme.palette.mode === "dark"
                ? alpha(theme.palette.text.primary, 0.68)
                : theme.palette.grey[500],
            maxWidth: 720,
            mb: 3,
            fontSize: { xs: "0.8125rem", sm: "0.875rem" },
          })}
        >
          Donji tablicama možete provjeriti globalnu pokrivenost i stanje po
          pojedinom savezu prije nego što u &ldquo;Pretraga&rdquo; suzite
          rezultate filterima. Trenutno limitirano isključivo na saveze u Gradu
          Zagrebu.
        </Typography>
        <Button
          variant="contained"
          color="secondary"
          size="large"
          fullWidth
          sx={{ maxWidth: { sm: 280 } }}
          startIcon={<Search />}
          onClick={() => onNavigate("/savezi")}
        >
          Pretraga saveza
        </Button>
      </Box>

      {stats && (
        <Grid
          container
          spacing={{ xs: 1.5, sm: 2 }}
          sx={{ mt: { xs: 1.5, sm: 2 } }}
        >
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <HeroStatCard
              label="SAVEZA U REGISTRU"
              value={stats.totalAlliances}
              subtitle="Aktivnih izvora u bazi"
              color="secondary.main"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <HeroStatCard
              label="ZADNJE AŽURIRANJE"
              value={lastUpdatedLabel}
              subtitle="Najnovija promjena zapisa"
              color="success.main"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <HeroStatCard
              label="POKRIVENOST DETALJA"
              value={`${stats.coverage?.details?.percentage ?? 0}%`}
              subtitle="Saveza s osnovnim podacima"
              color="secondary.light"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <HeroStatCard
              label="STAVKI VIJESTI"
              value={newsN}
              subtitle="Ukupno prikupljenih unosa"
              color="secondary.main"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <HeroStatCard
              label="ZAPISA ČLANSTVA"
              value={membersN}
              subtitle="Klubovi, udruge, članice"
              color="success.main"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <HeroStatCard
              label="STAVKI DOKUMENATA"
              value={documentsN}
              subtitle="Datoteke i zapisi dokumentacije"
              color="secondary.light"
            />
          </Grid>
        </Grid>
      )}
    </Paper>
  );
}

function HeroStatCard({
  label,
  value,
  subtitle,
  color,
}: {
  label: string;
  value: any;
  subtitle: string;
  color: string;
}) {
  return (
    <Box
      sx={(theme) => ({
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor:
          theme.palette.mode === "dark"
            ? alpha(theme.palette.common.white, 0.06)
            : alpha(theme.palette.common.black, 0.03),
        p: { xs: 2, sm: 3 },
        borderRadius: 2,
        border: `1px solid ${
          theme.palette.mode === "dark"
            ? alpha(theme.palette.common.white, 0.12)
            : theme.palette.divider
        }`,
      })}
    >
      <Typography
        variant="caption"
        sx={{ color, display: "block", mb: 0.75, flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Typography
        variant="h2"
        sx={{
          color: "inherit",
          flex: 1,
          display: "flex",
          alignItems: "center",
          minHeight: { xs: 44, sm: 52 },
          fontSize: { xs: "1.15rem", sm: "1.4rem", md: "1.5rem" },
          lineHeight: 1.25,
          wordBreak: "break-word",
        }}
      >
        {value}
      </Typography>
      <Typography
        variant="body2"
        sx={(theme) => ({
          color:
            theme.palette.mode === "dark"
              ? theme.palette.text.secondary
              : theme.palette.grey[500],
          fontSize: { xs: "0.75rem", sm: "0.875rem" },
          mt: 1,
          flexShrink: 0,
        })}
      >
        {subtitle}
      </Typography>
    </Box>
  );
}
